// DLC 11 — zakładka „Rysunek": rysunki przedsięwzięcia oglądane bez CAD-a na komputerze.
//
// Silnik żyje w ramce jako osobny dokument (patrz viewer/README.md). Ramka nie dostaje
// izolacji między-źródłowej, więc formalnie działa jednowątkowo — zmierzone: to NIC nie
// zmienia, bo parser silnika i tak jedzie jednym rdzeniem (20 s vs 19 s na tym samym
// rysunku). Dlatego zakładka jest równie szybka co osobna karta, a wygodniejsza.
//
// Tryby pokazywania zmieniają WYŁĄCZNIE klasy tego samego elementu. Ramki nie wolno
// przenieść w drzewie ani ukryć przez odmontowanie: rysunek parsowałby się od zera,
// a przy dużej mapie to kilka minut czekania za nic.
import { useEffect, useState } from "react";
import {
  ExternalLink,
  FileWarning,
  Maximize2,
  PenTool,
  Star,
  X,
} from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { useRysunki } from "../hooks";
import { adresRysunku, otworzRysunek, type Rysunek } from "../cadViewer";
import type { PowerProject } from "../types";
import { apiErr } from "../utils";

// Tryb „okno" (pływające, przesuwane) jest CHWILOWO WYŁĄCZONY: przy przełączeniu
// zakładki przeglądarki okno się rozjeżdża. Zgłoszone jako usterka techniczna —
// wrócimy do tego osobno, a nie po cichu przy okazji.
type Tryb = "zakladka" | "pelny";

const rozmiarTekstem = (bajty: number | null): string => {
  if (!bajty) return "";
  return bajty >= 1_000_000
    ? `${(bajty / 1_000_000).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bajty / 1000))} kB`;
};

// Próg, powyżej którego uprzedzamy, że otwarcie potrwa. Czas rośnie z KWADRATEM liczby
// obiektów, a jej z listy nie znamy — wielkość pliku jest jedyną przesłanką, jaką mamy.
const DUZY_RYSUNEK = 20_000_000;

function Wiersz({
  r,
  aktywny,
  naWybor,
}: {
  r: Rysunek;
  aktywny: boolean;
  naWybor: (r: Rysunek) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => naWybor(r)}
      className={`w-full text-left px-3 py-2 border-b border-[var(--color-border)] transition-colors ${
        aktywny
          ? "bg-[var(--color-primary)] text-[var(--color-text-inverse)]"
          : "hover:bg-[var(--color-bg-hover)]"
      }`}
    >
      <p className="text-sm font-medium truncate" title={r.nazwa}>
        {r.nazwa}
      </p>
      <p
        className={`text-[11px] font-mono truncate ${
          aktywny
            ? "text-[var(--color-text-inverse)] opacity-80"
            : "text-[var(--color-text-muted)]"
        }`}
      >
        {r.skad}
        {r.rozmiar ? ` · ${rozmiarTekstem(r.rozmiar)}` : ""}
      </p>
    </button>
  );
}

/**
 * Lista rysunków przedsięwzięcia.
 *
 * Obowiązujący przebieg jest ODDZIELONY od reszty, a nie tylko pierwszy na liście.
 * Wariantów i wersji bywa kilkanaście, a nazwy plików są często identyczne co do znaku
 * („linie qgz sti warstwa.dxf" siedem razy) — sama kolejność nie chroni przed pomyłką,
 * bo nikt nie pamięta, że pierwszy wiersz znaczy coś innego niż pozostałe.
 */
function ListaRysunkow({
  rysunki,
  wybrany,
  naWybor,
}: {
  rysunki: Rysunek[];
  wybrany: string | null;
  naWybor: (r: Rysunek) => void;
}) {
  const glowny = rysunki.find((r) => r.glowny) ?? null;
  const pozostale = rysunki.filter((r) => r !== glowny);

  return (
    <div className="w-72 flex-shrink-0 border-r border-[var(--color-border)] overflow-y-auto bg-[var(--color-bg)]">
      {glowny && (
        <div className="border-b-4 border-[var(--color-primary)]">
          <p className="px-3 pt-2 pb-1 text-[10px] font-mono font-bold tracking-wider text-[var(--color-primary)] flex items-center gap-1.5">
            <Star size={11} className="fill-current" /> OBOWIĄZUJĄCA TRASA
          </p>
          <div className="border-l-4 border-[var(--color-primary)]">
            <Wiersz
              r={glowny}
              aktywny={glowny.id === wybrany}
              naWybor={naWybor}
            />
          </div>
        </div>
      )}
      {pozostale.length > 0 && (
        <p className="px-3 pt-3 pb-1 text-[10px] font-mono tracking-wider text-[var(--color-text-muted)]">
          {glowny ? "POZOSTAŁE RYSUNKI" : "RYSUNKI PRZEDSIĘWZIĘCIA"}
        </p>
      )}
      {pozostale.map((r) => (
        <Wiersz key={r.id} r={r} aktywny={r.id === wybrany} naWybor={naWybor} />
      ))}
    </div>
  );
}

export function DrawingTab({ connection }: { connection: PowerProject }) {
  const { data: rysunki, isLoading, error } = useRysunki(connection.id);
  const [wybrany, setWybrany] = useState<Rysunek | null>(null);
  const [adres, setAdres] = useState<string | null>(null);
  const [bladOtwarcia, setBladOtwarcia] = useState("");
  const [tryb, setTryb] = useState<Tryb>("zakladka");

  // Escape wraca do zakładki — tego odruchu ludzie próbują pierwszego.
  useEffect(() => {
    if (tryb === "zakladka") return;
    const naKlawisz = (e: KeyboardEvent) => {
      if (e.key === "Escape") setTryb("zakladka");
    };
    window.addEventListener("keydown", naKlawisz);
    return () => window.removeEventListener("keydown", naKlawisz);
  }, [tryb]);

  // Obowiązującą trasę otwieramy sami — po nią przychodzi większość ludzi, a każdy
  // dodatkowy wybór to okazja do pomyłki między wersjami o tej samej nazwie.
  useEffect(() => {
    if (wybrany || !rysunki?.length) return;
    const glowny = rysunki.find((r) => r.glowny);
    if (glowny) setWybrany(glowny);
  }, [rysunki, wybrany]);

  // Bilet na rysunek żyje 5 minut, więc bierzemy go dopiero przy wyborze — a nie
  // hurtem dla całej listy, gdzie i tak zdążyłby wygasnąć.
  useEffect(() => {
    if (!wybrany) return;
    let aktualny = true;
    setAdres(null);
    setBladOtwarcia("");
    adresRysunku(wybrany.id)
      .then((a) => {
        if (aktualny) setAdres(a);
      })
      .catch((e) => {
        if (aktualny)
          setBladOtwarcia(apiErr(e, "Nie udało się otworzyć rysunku."));
      });
    return () => {
      aktualny = false;
    };
  }, [wybrany]);

  if (isLoading) {
    return (
      <div className="p-8 flex justify-center">
        <Spinner size="lg" />
      </div>
    );
  }
  if (error) {
    return (
      <p className="p-6 text-sm text-[var(--color-primary)]">
        {apiErr(error, "Nie udało się wczytać listy rysunków.")}
      </p>
    );
  }
  if (!rysunki?.length) {
    return (
      <div className="p-8 text-center text-[var(--color-text-muted)]">
        <FileWarning size={28} className="mx-auto mb-3" />
        <p className="text-sm">
          To przedsięwzięcie nie ma jeszcze rysunków DXF ani DWG.
        </p>
        <p className="text-xs mt-1">
          Pojawią się tu same, gdy trafią do trasy albo do zakładki Pliki.
        </p>
      </div>
    );
  }

  // „Wyjęty" = rysunek zajmuje cały ekran. Wtedy nie ma po co pokazywać naszej listy
  // ani stopki — cała powierzchnia należy do rysunku.
  const wyjety = tryb !== "zakladka";

  const klasyRamy =
    tryb === "pelny"
      ? "fixed inset-1 z-50 flex flex-col bg-[var(--color-bg)] border border-[var(--color-border)] shadow-2xl"
      : "flex flex-col h-[calc(100vh-220px)] min-h-[380px] max-h-[calc(100vh-220px)] border border-[var(--color-border)] bg-[var(--color-bg)]";

  return (
    <>
      {/* Przyciemnienie tylko na pełnym ekranie. Okno pływające ma być oknem — spod
          niego musi być widać, co dzieje się w aplikacji. */}
      {tryb === "pelny" && (
        <div
          className="fixed inset-0 bg-black/70 z-40"
          onClick={() => setTryb("zakladka")}
        />
      )}

      <div className={klasyRamy}>
        <div
          className={`flex items-center justify-between border-b border-[var(--color-border)] gap-3 bg-[var(--color-bg)] ${
            wyjety ? "px-2 py-1" : "px-3 py-2"
          }`}
        >
          <p
            className={`font-medium truncate flex items-center gap-2 min-w-0 ${wyjety ? "text-xs" : "text-sm"}`}
          >
            {wybrany?.glowny && (
              <span className="flex items-center gap-1 text-[10px] font-mono font-bold tracking-wider text-[var(--color-text-inverse)] bg-[var(--color-primary)] px-1.5 py-0.5 flex-shrink-0">
                <Star size={10} className="fill-current" /> OBOWIĄZUJĄCA
              </span>
            )}
            <span className="truncate">
              {wybrany ? wybrany.nazwa : "Wybierz rysunek z listy po lewej"}
            </span>
          </p>

          <div className="flex items-center gap-3 flex-shrink-0">
            {/* Nazwa silnika w pasku, a nie w osobnej stopce na dole: stopka była
                dodatkowym paskiem, który przy niższym oknie wychodził poza widok
                i nachodził na stronę. Tu jest widoczna i nic nie zasłania. */}
            {!wyjety && (
              <span className="hidden lg:inline text-[11px] text-[var(--color-text-muted)]">
                Silnik: ELGW5 CAD
              </span>
            )}
            {tryb !== "pelny" && (
              <button
                type="button"
                className="flex items-center gap-1.5 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
                title="Cały ekran — sam rysunek"
                onClick={() => setTryb("pelny")}
              >
                <Maximize2 size={14} />
                {!wyjety && "Pełny ekran"}
              </button>
            )}
            {wybrany && !wyjety && (
              <button
                type="button"
                className="flex items-center gap-1.5 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
                title="Otwórz ten rysunek w osobnej karcie przeglądarki"
                onClick={() => {
                  otworzRysunek(wybrany.id).catch((e) =>
                    setBladOtwarcia(
                      apiErr(e, "Nie udało się otworzyć rysunku."),
                    ),
                  );
                }}
              >
                <ExternalLink size={14} /> Nowa karta
              </button>
            )}
            {wyjety && (
              <button
                type="button"
                className="p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
                title="Wróć do zakładki (Esc)"
                onClick={() => setTryb("zakladka")}
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-1 min-h-0">
          {/* Lista znika Z POLA WIDZENIA, ale nie z drzewa: stoi obok ramki z rysunkiem,
              a każda przebudowa tego miejsca kosztowałaby ponowne wczytanie pliku. */}
          <div className={wyjety ? "hidden" : "contents"}>
            <ListaRysunkow
              rysunki={rysunki}
              wybrany={wybrany?.id ?? null}
              naWybor={setWybrany}
            />
          </div>

          <div className="flex-1 min-w-0 relative">
            {!wybrany && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-[var(--color-text-muted)] gap-2">
                <PenTool size={30} />
                <p className="text-sm">
                  Rysunek otworzy się tutaj — bez CAD-a na komputerze.
                </p>
              </div>
            )}
            {bladOtwarcia && (
              <div className="absolute inset-0 flex items-center justify-center p-6">
                <p className="text-sm text-[var(--color-primary)] text-center">
                  {bladOtwarcia}
                </p>
              </div>
            )}
            {wybrany && !adres && !bladOtwarcia && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                <Spinner size="lg" />
                {wybrany.rozmiar && wybrany.rozmiar > DUZY_RYSUNEK && (
                  <p className="text-xs text-[var(--color-text-muted)] text-center max-w-sm">
                    Duży rysunek — otwarcie może potrwać kilka minut. Możesz w
                    tym czasie pracować w innej zakładce.
                  </p>
                )}
              </div>
            )}
            {adres && (
              <iframe
                key={adres}
                src={adres}
                title={wybrany?.nazwa ?? "Rysunek"}
                className="w-full h-full border-0"
              />
            )}
          </div>
        </div>

      </div>
    </>
  );
}
